import React, {CSSProperties, useState} from 'react'

import {FieldProps} from './types.js'
import {choiceTokens, getChoiceBoxColors, getChoiceOverlayStyle} from './choiceStyles.js'

// The label is the content of the round option itself (e.g. a weekday letter), so there is no room for info:
// it is accepted as part of FieldProps but not rendered
export interface OptionProps extends FieldProps {
    checked?: boolean
    onChange?: (event: {target: {checked: boolean}}) => void
}

const Option = ({disabled, readOnly, checked, onChange, label, style = {}}: OptionProps) => {
    const [focused, setFocused] = useState(false)
    const [hover, setHover] = useState<boolean>()

    // Read only keeps the normal look but ignores clicks and keys, like disabled
    const interactive = !disabled && !readOnly

    const toggle = () => {
        if (interactive) {
            onChange?.({target: {checked: !checked}})
        }
    }

    // Clicking focuses the option too, but the focus highlight is only shown again once a key is pressed
    const onClick = () => {
        toggle()
        setFocused(false)
    }

    // Space behaves like a click; preventDefault stops the page from scrolling
    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        setFocused(true)

        if (event.key === ' ' || event.key === 'Spacebar' || event.keyCode === 32) {
            event.preventDefault()
            toggle()
        }
    }

    // The overlay is centred in the row like in the other choice controls
    const overLayStyle = getChoiceOverlayStyle({highlighted: hover || focused, interactive})

    // Checked uses the shared checked colours; unchecked is a filled grey circle instead of a bordered box
    const checkedColors = getChoiceBoxColors({checked: true, disabled})

    const checkboxStyle: CSSProperties = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: checked ? checkedColors.background : disabled ? choiceTokens.uncheckedDisabledBackground : choiceTokens.optionUncheckedBackground,
        width: 26,
        height: 26,
        borderRadius: '50%',
        fontSize: 14,
        color: checked ? checkedColors.foreground : disabled ? choiceTokens.optionDisabledForeground : choiceTokens.optionUncheckedForeground,
        transition: 'background .1s ease',
        outline: 'none'
    }

    const defaultStyle: CSSProperties = {display: 'flex', alignItems: 'center', height: choiceTokens.height, ...choiceTokens.margins.option, ...style}

    return (
        <div style={defaultStyle}>
            <div style={overLayStyle} onClick={onClick}>
                <div
                    tabIndex={disabled ? -1 : 0}
                    role='checkbox'
                    aria-checked={!!checked}
                    aria-disabled={disabled || undefined}
                    aria-readonly={readOnly || undefined}
                    style={checkboxStyle}
                    onKeyDown={onKeyDown}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                >
                    {label}
                </div>
            </div>
        </div>
    )
}

export default Option
